import React, { useState, useEffect } from 'react';
import { Upload, Download, FolderOpen, Trash2, Search, FileText, File, FileSpreadsheet, FileImage, CheckSquare, Square, Eye, Folder, FolderPlus, MoreVertical, Edit2, FolderDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import securityDocumentsAPI from '../../services/securityDocumentsAPI';
import api from '../../api';
import UploadDocumentModal from './UploadDocumentModal';
import CreateFolderModal from './CreateFolderModal';
import EditFolderModal from './EditFolderModal';
import ConfirmDialog from './ConfirmDialog';
import Pagination from '../Common/Pagination';
import toast from 'react-hot-toast';

interface SecurityDocument {
  id: number;
  title: string;
  description: string | null;
  original_name: string;
  file_size: number;
  mime_type: string;
  uploaded_by: number;
  uploader_name: string;
  uploader_email: string;
  created_at: string;
  download_count: number;
  tags: string[];
}

const SecurityDocumentRepository: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'SUPER_ADMIN';

  const [documents, setDocuments] = useState<SecurityDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<SecurityDocument | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(3);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Folder state
  const [folders, setFolders] = useState<any[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [folderMenuOpen, setFolderMenuOpen] = useState<number | null>(null);
  const [editingFolder, setEditingFolder] = useState<any | null>(null);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    loading: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    loading: false
  });

  // Fetch folders
  const fetchFolders = async () => {
    try {
      setFoldersLoading(true);
      const response = await securityDocumentsAPI.getFolders();
      setFolders(response.folders || []);
    } catch (error: any) {
      console.error('Error fetching folders:', error);
    } finally {
      setFoldersLoading(false);
    }
  };

  // Fetch documents
  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await securityDocumentsAPI.getDocuments(
        searchTerm || undefined,
        currentPage,
        itemsPerPage,
        currentFolderId
      );
      setDocuments(response.documents || []);
      if (response.pagination) {
        setTotalPages(response.pagination.totalPages);
        setTotalItems(response.pagination.totalItems);
      }
    } catch (error: any) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFolders();
    fetchDocuments();
  }, []);

  // Re-fetch documents when folder changes
  useEffect(() => {
    fetchDocuments();
  }, [currentFolderId]);

  // Auto-search with debounce
  useEffect(() => {
    const delayTimer = setTimeout(() => {
      // Reset to page 1 when search term changes
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchDocuments();
      }
    }, 500); // 500ms delay after typing stops

    return () => clearTimeout(delayTimer);
  }, [searchTerm]);

  // Fetch when page changes
  useEffect(() => {
    fetchDocuments();
  }, [currentPage]);

  // Close folder menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (folderMenuOpen !== null) {
        // Check if click is outside the dropdown menu
        const target = event.target as HTMLElement;
        const isClickInsideDropdown = target.closest('.folder-dropdown-menu');
        const isClickOnThreeDots = target.closest('.folder-three-dots');

        if (!isClickInsideDropdown && !isClickOnThreeDots) {
          setFolderMenuOpen(null);
        }
      }
    };

    if (folderMenuOpen !== null) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [folderMenuOpen]);

  // Handle search
  const handleSearch = () => {
    fetchDocuments();
  };

  // Handle download
  const handleDownload = async (doc: SecurityDocument) => {
    try {
      toast.loading('Downloading...', { id: 'download-loading' });
      await securityDocumentsAPI.downloadDocument(doc.id, doc.original_name);
      toast.dismiss('download-loading');
      toast.success('Download started');
      fetchDocuments();
    } catch (error) {
      toast.dismiss('download-loading');
      console.error('Download error:', error);
      toast.error('Failed to download document');
    }
  };

  // Handle bulk download
  const handleBulkDownload = async () => {
    if (selectedDocs.size === 0) {
      toast.error('Please select at least one document');
      return;
    }

    try {
      setBulkDownloading(true);
      await securityDocumentsAPI.bulkDownload(Array.from(selectedDocs));
      toast.success(`Downloaded ${selectedDocs.size} documents as ZIP`);
      setSelectedDocs(new Set());
      // Refresh to update download counts
      fetchDocuments();
    } catch (error) {
      console.error('Bulk download error:', error);
      toast.error('Failed to download documents');
    } finally {
      setBulkDownloading(false);
    }
  };

  // Handle delete
  const handleDelete = async (docId: number) => {
    const doc = documents.find(d => d.id === docId);
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Document',
      message: `Are you sure you want to delete "${doc?.title}"? This action cannot be undone.`,
      loading: false,
      onConfirm: async () => {
        try {
          setConfirmDialog(prev => ({ ...prev, loading: true }));
          await securityDocumentsAPI.deleteDocument(docId);
          toast.success('Document deleted successfully');
          fetchDocuments();
          fetchFolders();
        } catch (error) {
          console.error('Delete error:', error);
          toast.error('Failed to delete document');
        } finally {
          setConfirmDialog(prev => ({ ...prev, isOpen: false, loading: false }));
        }
      }
    });
  };

  // Handle folder download (download all documents in folder)
  const handleFolderDownload = async (folderId: number, folderName: string) => {
    try {
      // Get all documents in this folder
      const response = await securityDocumentsAPI.getDocuments(undefined, 1, 1000, folderId);
      const folderDocs = response.documents || [];

      if (folderDocs.length === 0) {
        toast.error('Folder is empty');
        return;
      }

      const docIds = folderDocs.map((doc: any) => doc.id);
      await securityDocumentsAPI.bulkDownload(docIds);
      toast.success(`Downloaded ${folderDocs.length} documents from ${folderName}`);
      fetchFolders();
      fetchDocuments();
    } catch (error) {
      console.error('Folder download error:', error);
      toast.error('Failed to download folder');
    }
  };

  // Handle folder delete (deletes folder AND all documents inside)
  const handleFolderDelete = async (folderId: number, folderName: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Folder',
      message: `Delete folder "${folderName}"? This action cannot be undone.`,
      loading: false,
      onConfirm: async () => {
        try {
          setConfirmDialog(prev => ({ ...prev, loading: true }));
          await securityDocumentsAPI.deleteFolder(folderId);
          toast.success('Folder deleted successfully');
          fetchFolders();
          if (currentFolderId === folderId) {
            setCurrentFolderId(null);
          }
          fetchDocuments();
          setFolderMenuOpen(null);
        } catch (error: any) {
          console.error('Delete folder error:', error);
          toast.error(error.response?.data?.error || 'Failed to delete folder');
        } finally {
          setConfirmDialog(prev => ({ ...prev, isOpen: false, loading: false }));
        }
      }
    });
  };

  // Handle delete all folders
  const handleDeleteAllFolders = async () => {
    const folderCount = folders.length;

    setConfirmDialog({
      isOpen: true,
      title: 'Delete All Folders',
      message: `Delete all ${folderCount} folder${folderCount > 1 ? 's' : ''}? This action cannot be undone.`,
      loading: false,
      onConfirm: async () => {
        try {
          setConfirmDialog(prev => ({ ...prev, loading: true }));
          // Delete all folders one by one
          for (const folder of folders) {
            await securityDocumentsAPI.deleteFolder(folder.id);
          }
          toast.success(`All ${folderCount} folder${folderCount > 1 ? 's' : ''} deleted successfully`);
          setCurrentFolderId(null);
          fetchFolders();
          fetchDocuments();
        } catch (error: any) {
          console.error('Delete all folders error:', error);
          toast.error(error.response?.data?.error || 'Failed to delete all folders');
        } finally {
          setConfirmDialog(prev => ({ ...prev, isOpen: false, loading: false }));
        }
      }
    });
  };

  // Toggle selection
  const toggleSelect = (docId: number) => {
    const newSelected = new Set(selectedDocs);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedDocs(newSelected);
  };

  // Select all
  const handleSelectAll = () => {
    if (selectedDocs.size === documents.length) {
      setSelectedDocs(new Set());
    } else {
      setSelectedDocs(new Set(documents.map(d => d.id)));
    }
  };

  // Get file icon
  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (mimeType.includes('word')) return <FileText className="w-5 h-5 text-blue-500" />;
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
    if (mimeType.includes('image')) return <FileImage className="w-5 h-5 text-purple-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'N/A';
    }
  };

  // Handle preview - opens in new tab
  const handlePreview = async (doc: SecurityDocument) => {
    try {
      toast.loading('Opening preview...', { id: 'preview-loading' });
      const response = await api.get(`/api/security-documents/${doc.id}/download?preview=true`, {
        responseType: 'blob'
      });

      const blob = response.data;

      if (blob.size === 0) {
        throw new Error('Received empty file');
      }

      const url = URL.createObjectURL(blob);
      toast.dismiss('preview-loading');
      window.open(url, '_blank');
    } catch (error) {
      toast.dismiss('preview-loading');
      console.error('Preview error:', error);
      toast.error('Failed to load preview');
    }
  };

  // Cleanup preview URL when closing
  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
    }
    setPreviewDoc(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FolderOpen className="w-8 h-8 text-blue-600" />
            Security Documents
          </h2>
          <p className="text-gray-600 mt-1">
            Access security policies, compliance documents, and organizational standards
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
          >
            <Upload className="w-4 h-4" />
            Upload Document
          </button>
        )}
      </div>

      {/* Folders Section */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Folder className="w-5 h-5 text-blue-600" />
            Folders
          </h3>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCreateFolderModal(true)}
                className="px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 flex items-center gap-2 transition-colors text-sm"
              >
                <FolderPlus className="w-4 h-4" />
                New Folder
              </button>
              {folders.length > 1 && (
                <button
                  onClick={handleDeleteAllFolders}
                  className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 flex items-center gap-2 transition-colors text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete All
                </button>
              )}
            </div>
          )}
        </div>

        {foldersLoading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-sm text-gray-500 mt-3">Loading folders...</p>
          </div>
        ) : folders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Folder className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm">No folders yet. Create your first folder to organize documents.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* User Folders */}
            {folders.map((folder: any) => (
                <div
                  key={folder.id}
                  className={`p-4 border-2 rounded-lg transition-all group relative overflow-visible ${
                    currentFolderId === folder.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  }`}
                >
                <div onClick={() => setCurrentFolderId(folder.id)} className="cursor-pointer">
                  <div className="flex items-center gap-3 mb-2">
                    <Folder className={`w-8 h-8 ${currentFolderId === folder.id ? 'text-blue-600' : 'text-gray-400'}`} />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{folder.name}</div>
                      <div className="text-sm text-gray-500">{folder.document_count || 0} items</div>
                    </div>
                  </div>
                  {folder.description && (
                    <p className="text-xs text-gray-500 mt-2">{folder.description}</p>
                  )}
                </div>

                {/* Three-dot menu (SUPER_ADMIN only) */}
                {isAdmin && (
                  <div className="absolute top-2 right-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFolderMenuOpen(folderMenuOpen === folder.id ? null : folder.id);
                      }}
                      className="folder-three-dots p-1 rounded hover:bg-gray-200 transition-colors"
                    >
                      <MoreVertical className="w-4 h-4 text-gray-600" />
                    </button>

                    {/* Dropdown Menu */}
                    {folderMenuOpen === folder.id && (
                      <div className="folder-dropdown-menu absolute right-full top-0 mr-2 w-48 bg-white rounded-lg shadow-2xl border border-gray-200 z-[100]">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFolderDownload(folder.id, folder.name);
                            setFolderMenuOpen(null);
                          }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 rounded-t-lg transition-colors"
                        >
                          <FolderDown className="w-4 h-4" />
                          Download Folder
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingFolder(folder);
                            setFolderMenuOpen(null);
                          }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors border-t border-gray-100"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit Folder
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFolderDelete(folder.id, folder.name);
                          }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2 rounded-b-lg transition-colors border-t border-gray-100"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Folder
                        </button>
                      </div>
                    )}
                  </div>
                )}
            </div>
          ))}
          </div>
        )}

        {/* Current Folder Indicator */}
        {currentFolderId && (
          <div className="mt-4 flex items-center gap-2">
            <button
              onClick={() => setCurrentFolderId(null)}
              className="text-sm text-blue-600 hover:underline"
            >
              ← Back to all folders
            </button>
            <span className="text-sm text-gray-500">|</span>
            <span className="text-sm font-medium text-gray-900">
              Viewing: {folders.find((f: any) => f.id === currentFolderId)?.name}
            </span>
          </div>
        )}
      </div>

      {/* Search & Bulk Actions */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search documents..."
              className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            Search
          </button>

          {/* Bulk Download */}
          {selectedDocs.size > 0 && (
            <button
              onClick={handleBulkDownload}
              disabled={bulkDownloading}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2 transition-colors disabled:bg-gray-400"
            >
              <Download className="w-4 h-4" />
              {bulkDownloading ? 'Downloading...' : `Download Selected (${selectedDocs.size})`}
            </button>
          )}
        </div>
      </div>

      {/* Documents Table */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2">Loading documents...</p>
          </div>
        ) : documents.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-lg font-medium">No documents found</p>
            <p className="text-sm mt-1">
              {isAdmin ? 'Upload your first document to get started' : 'No documents available yet'}
            </p>
          </div>
        ) : (
          <>
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 w-12">
                  <button onClick={handleSelectAll}>
                    {selectedDocs.size === documents.length && documents.length > 0 ? (
                      <CheckSquare className="w-5 h-5 text-blue-600" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Document
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Uploaded By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Upload Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {documents.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4">
                    <button onClick={() => toggleSelect(doc.id)}>
                      {selectedDocs.has(doc.id) ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-start gap-3">
                      {getFileIcon(doc.mime_type)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {doc.title}
                        </p>
                        {doc.description && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                            {doc.description}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">{doc.original_name}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatFileSize(doc.file_size)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div>
                      <p className="font-medium text-gray-900">{doc.uploader_name}</p>
                      <p className="text-xs text-gray-500">{doc.uploader_email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(doc.created_at)}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handlePreview(doc)}
                        className="text-purple-600 hover:text-purple-800 transition-colors"
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDownload(doc)}
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => handleDelete(doc.id)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

        {/* Pagination */}
        {documents.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            itemName="documents"
          />
        )}
          </>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadDocumentModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            fetchDocuments();
            fetchFolders();
          }}
          folders={folders}
          currentFolderId={currentFolderId}
        />
      )}

      {/* Create Folder Modal */}
      <CreateFolderModal
        isOpen={showCreateFolderModal}
        onClose={() => setShowCreateFolderModal(false)}
        onFolderCreated={() => {
          fetchFolders();
          fetchDocuments();
        }}
      />

      {/* Edit Folder Modal */}
      <EditFolderModal
        folder={editingFolder}
        onClose={() => setEditingFolder(null)}
        onFolderUpdated={() => {
          fetchFolders();
          fetchDocuments();
        }}
      />

      {/* Preview Modal */}
      {previewDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {previewDoc.title}
                </h3>
                <p className="text-sm text-gray-500">{previewDoc.original_name}</p>
              </div>
              <button
                onClick={closePreview}
                className="ml-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            {/* Preview Content */}
            <div className="p-6 overflow-auto max-h-[calc(90vh-180px)]">
              {!previewUrl ? (
                <div className="flex items-center justify-center h-[600px]">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : previewDoc.mime_type === 'application/pdf' ? (
                <object
                  data={previewUrl}
                  type="application/pdf"
                  className="w-full h-[600px] border rounded"
                >
                  <embed src={previewUrl} type="application/pdf" className="w-full h-[600px]" />
                </object>
              ) : previewDoc.mime_type.startsWith('image/') ? (
                <img
                  src={previewUrl}
                  alt={previewDoc.title}
                  className="max-w-full h-auto mx-auto"
                />
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">
                    Preview not available for this file type
                  </p>
                  <button
                    onClick={() => handleDownload(previewDoc)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 inline-flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download to View
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
              <div className="text-sm text-gray-600">
                <p>Uploaded by: {previewDoc.uploader_name}</p>
                <p>Size: {formatFileSize(previewDoc.file_size)}</p>
              </div>
              <button
                onClick={() => handleDownload(previewDoc)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 inline-flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        variant="danger"
        loading={confirmDialog.loading}
      />
    </div>
  );
};

export default SecurityDocumentRepository;
